// frontend/app/components/lessonPlans/lessonThumbnail.js
//
// Turns a resolved asset's `thumbnail_url` into something an <Image> can load.
//
// The backend sends the content-store path it already catalogues (the same
// kind of path the curriculum tiles use). Those need a short-lived signed URL
// from /proxy_contentimageurl/, which wants the viewer's token, so the signing
// happens here. Results are cached per path for the life of the page: a lesson
// re-renders constantly, and a path signs the same way every time. Signed URLs
// expire, so a success is only trusted for SIGNED_URL_TTL_MS, a failure is
// never cached, and a card whose <Image> fails to load can evict its path.

import { useEffect, useState } from "react";

import { useAuth } from "../../AuthContext";
import { FASTAPI_BASE_URL } from "../../config";

const ABSOLUTE_URL = /^(https?:|data:|blob:)/i;
const CONTENT_PREFIX = "content_streaming/image";

/** How long a signed URL is reused before it is signed again. */
export const SIGNED_URL_TTL_MS = 10 * 60 * 1000;

// path -> { promise, value, expiresAt }
const cache = new Map();

export const __resetLessonThumbnailCache = () => cache.clear();

/** Forget a path so the next mount signs it afresh (call when its image 404s). */
export const evictLessonThumbnail = (thumbnailPath) => {
  const path = typeof thumbnailPath === "string" ? thumbnailPath.trim() : "";
  if (path) cache.delete(path);
};

const freshValue = (path) => {
  const entry = path ? cache.get(path) : null;
  return entry && entry.value && entry.expiresAt > Date.now() ? entry.value : null;
};

const normalize = (value) =>
  String(value || "")
    .replace(CONTENT_PREFIX, "")
    .replace(/\/{2,}/g, "/")
    .trim();

const pickSignedUrl = (entries, path) => {
  const list = Array.isArray(entries) ? entries : [];
  const wanted = normalize(path);
  const match = list.find((entry) => normalize(entry?.key) === wanted);
  if (match?.url) return match.url;
  // One path asked, one answer back: that answer is ours.
  return list.length === 1 && list[0]?.url ? list[0].url : null;
};

const signPath = async (fetchWithAuth, path) => {
  try {
    const res = await fetchWithAuth(`${FASTAPI_BASE_URL}/proxy_contentimageurl/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ image_path_list: [path] }),
      rawAuth: true,
    });
    if (!res || !res.ok) return null;
    const body = await res.json();
    return pickSignedUrl(body?.data?.url_list, path);
  } catch (error) {
    // A missing picture is not worth interrupting a lesson for; the card falls
    // back to its tile. Leave a trace for whoever wonders why.
    console.warn("[lesson thumbnail] could not sign image path", error);
    return null;
  }
};

/** `thumbnailPath` -> a loadable URL, or null while unknown / unavailable. */
export const useLessonThumbnail = (thumbnailPath) => {
  const auth = useAuth();
  const fetchWithAuth = auth?.fetchWithAuth;
  const path = typeof thumbnailPath === "string" ? thumbnailPath.trim() : "";
  const direct = path && ABSOLUTE_URL.test(path) ? path : null;

  const [signed, setSigned] = useState(() => freshValue(path));

  useEffect(() => {
    // The path changed: show what we know for the new one, never the old image.
    setSigned(freshValue(path));
    if (!path || direct || !fetchWithAuth) {
      setSigned(null);
      return undefined;
    }
    let cancelled = false;
    let entry = cache.get(path);
    const expired = entry && !entry.pending && !(entry.value && entry.expiresAt > Date.now());
    if (!entry || expired) {
      const created = { pending: true, value: null, expiresAt: 0, promise: null };
      created.promise = signPath(fetchWithAuth, path).then((value) => {
        if (cache.get(path) === created) {
          if (value) {
            created.pending = false;
            created.value = value;
            created.expiresAt = Date.now() + SIGNED_URL_TTL_MS;
          } else {
            cache.delete(path); // a failure is not worth remembering
          }
        }
        return value;
      });
      cache.set(path, created);
      entry = created;
    }
    entry.promise.then((value) => {
      if (!cancelled) setSigned(value);
    });
    return () => {
      cancelled = true;
    };
  }, [path, direct, fetchWithAuth]);

  return direct || signed;
};
